import { motion } from 'framer-motion'
import { AlertTriangle, MessageSquareText, Send } from 'lucide-react'
import type { StatsResponse } from '../api'
import { useTrend } from '../hooks/useTrend'
import { Skeleton } from './ui/skeleton'
import { Sparkline } from './Sparkline'

interface Props {
  stats: StatsResponse | undefined
  isLoading: boolean
}

const CARDS = [
  {
    key: 'total_messages' as const,
    label: 'Mensajes recibidos',
    icon: MessageSquareText,
    accent: 'from-sky-500/20 text-sky-600 ring-sky-500/30 dark:text-sky-300',
  },
  {
    key: 'total_responded' as const,
    label: 'Respondidos',
    icon: Send,
    accent: 'from-emerald-500/20 text-emerald-600 ring-emerald-500/30 dark:text-emerald-300',
  },
  {
    key: 'total_errors' as const,
    label: 'Errores',
    icon: AlertTriangle,
    accent: 'from-rose-500/20 text-rose-600 ring-rose-500/30 dark:text-rose-300',
  },
]

function StatCard({
  label,
  Icon,
  accent,
  value,
  isLoading,
  index,
}: {
  label: string
  Icon: typeof MessageSquareText
  accent: string
  value: number | undefined
  isLoading: boolean
  index: number
}) {
  const trend = useTrend(value)

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.06, ease: 'easeOut' }}
      whileHover={{ y: -3 }}
      className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white/70 p-5 shadow-sm backdrop-blur transition-shadow hover:shadow-lg dark:border-slate-800 dark:bg-slate-900/60 dark:shadow-black/20 dark:hover:shadow-black/30"
    >
      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-linear-to-br ring-1 ${accent}`}>
        <Icon className="h-5 w-5" strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        {isLoading || value === undefined ? (
          <Skeleton className="h-8 w-16" />
        ) : (
          <p className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-50">
            {value.toLocaleString('es-CR')}
          </p>
        )}
        <p className="text-sm text-slate-500 dark:text-slate-400">{label}</p>
      </div>
      {trend.length >= 2 && <Sparkline data={trend} className={`h-7 w-16 shrink-0 ${accent.match(/text-\S+/)?.[0] ?? ''}`} />}
    </motion.div>
  )
}

export function StatsCards({ stats, isLoading }: Props) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      {CARDS.map(({ key, label, icon, accent }, index) => (
        <StatCard
          key={key}
          label={label}
          Icon={icon}
          accent={accent}
          value={stats?.[key]}
          isLoading={isLoading}
          index={index}
        />
      ))}
    </div>
  )
}
